import {useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';

import * as uamMethods from '../consts/uam/uamMethods';
import * as uamfMethods from '../consts/uamf/uamfMethods';
import cacheService from '../services/cacheService';
import consentsService from '../services/consentsService';
import damClientService from '../services/dam/damClientService';
import damDeviceSettingsService from '../services/dam/damDeviceSettingsService';
import deviceHelpers from '../services/device/deviceHelpers';
import log from '../services/logger/log';
import {selectIsOnline} from '../state/selectors/global';
import {selectConnectedIotDevicesList, selectIotDeviceConnected} from '../state/selectors/iotDevices';
import {setIsDeviceUpdateSettingInProgress} from '../state/slice/iotDevices';
import {dispatch, getState} from '../state/store';

const SETTING_PATHS = [uamMethods.UPDATE_DEVICE_SETTING, uamfMethods.UPDATE_SETTING];

const isUpdateSettingPath = (path) => SETTING_PATHS.includes(path);

const isIlumaI5Request = ({data}) => deviceHelpers.getIsIlumaI5ByDusn(data?.serialNumber);

const useSyncOnlineRequests = () => {
    const isOnline = useSelector(selectIsOnline);
    const connectedDusnList = useSelector(selectConnectedIotDevicesList);
    const processingUrls = useRef(new Set());

    const sendRequest = (data) => {
        const {path, serialNumber, body} = data || {};

        if (isUpdateSettingPath(path)) {
            const {settingName, newValue} = body || {};

            // not callRequest: the setting stays in progress until the device confirms it over WSS
            return damDeviceSettingsService.syncUpdateSetting(serialNumber, settingName, newValue);
        }

        return damClientService.getClient(serialNumber).callRequest(data);
    };

    const handleRequest = async (url, data) => {
        try {
            prepareHandleRequest(data);

            await sendRequest(data);

            log.debug(`useSyncOnlineRequests: Request processed successfully, url: ${url}`);
        } catch (e) {
            log.error(`useSyncOnlineRequests: Error processing request, url: ${url}`, e?.message);
        } finally {
            completeHandleRequest(data);
        }
    };

    const prepareHandleRequest = (data) => {
        const {path, serialNumber, queryParams} = data || {};

        if (isUpdateSettingPath(path)) {
            const {settingName} = queryParams || {};

            dispatch(setIsDeviceUpdateSettingInProgress(serialNumber, settingName, true));
        }
    };

    const completeHandleRequest = (data) => {
        const {path, serialNumber, queryParams} = data || {};

        if (isUpdateSettingPath(path)) {
            const {settingName} = queryParams || {};

            dispatch(setIsDeviceUpdateSettingInProgress(serialNumber, settingName, false));
        } else if (path === uamMethods.USER_CONSENTS) {
            consentsService.updateUserConsentsFlagValues();
        }
    };

    const processCachedRequest = async (request) => {
        const {data, url} = request || {};
        const {serialNumber} = data || {};

        log.debug(`useSyncOnlineRequests: Processing request, url: ${url}`);

        if (processingUrls.current.has(url)) {
            log.debug(`useSyncOnlineRequests: Skip request, already in progress, url: ${url}`);
            return;
        }

        if (serialNumber) {
            const isDeviceConnected = selectIotDeviceConnected(serialNumber)(getState());

            if (!isDeviceConnected) {
                log.debug(`useSyncOnlineRequests: Skip request, device is not connected, url: ${url}`);
                return;
            }
        }

        processingUrls.current.add(url);

        try {
            await handleRequest(url, data);
            await cacheService.deleteCachedRequest(url);
        } finally {
            processingUrls.current.delete(url);
        }
    };

    const processRequestsInSequence = async (requests) => {
        for (const request of requests) {
            await processCachedRequest(request);
        }
    };

    const syncCachedRequests = async () => {
        try {
            const cachedRequests = await cacheService.getCachedOnlineRequests();

            if (!cachedRequests.length) return;

            log.debug('useSyncOnlineRequests: Starting to process cached requests');

            // i5 rejects any overlapping setting update with ACTIVE_REQUEST_ALREADY_EXISTS error
            const i5Requests = cachedRequests.filter(isIlumaI5Request);
            const otherRequests = cachedRequests.filter((request) => !isIlumaI5Request(request));

            await Promise.all([processRequestsInSequence(i5Requests), ...otherRequests.map(processCachedRequest)]);
        } catch (e) {
            log.error('useSyncOnlineRequests: Error processing cached requests', e?.message);
        }

        log.debug('useSyncOnlineRequests: Finish processing cached requests');
    };

    useEffect(() => {
        if (isOnline) {
            syncCachedRequests();
        }
    }, [isOnline, connectedDusnList.length]);
};

export default useSyncOnlineRequests;
